import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface MockAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  accessToken: string | null;
  user: {
    sub: string;
    name: string;
    email: string;
    preferred_username: string;
  } | null;
}

interface DebugAuthContextType {
  authState: MockAuthState | null;
  oktaAuth: {
    getAccessToken: () => Promise<string | undefined>;
  };
}

const DebugAuthContext = createContext<DebugAuthContextType | null>(null);

// Check if debug mode is enabled
function isDebugModeEnabled(): boolean {
  const sophiaApiKey = import.meta.env.VITE_SOPHIA_API_KEY;
  const debugLogin = import.meta.env.VITE_DEBUG_LOGIN;
  
  return !!(sophiaApiKey && debugLogin);
}

interface DebugAuthProviderProps {
  children: ReactNode;
}

// Helper function to create a valid JWT token for debug mode
function createDebugJWT(): string {
  // Create a minimal valid JWT structure with base64url encoded parts
  const header = {
    alg: 'none',
    typ: 'JWT'
  };
  
  const payload = {
    sub: 'debug-user-123',
    uid: 'debug-user-123',
    name: 'Debug User',
    email: 'debug@example.com',
    preferred_username: 'debug-user',
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 3600 // 1 hour
  };
  
  // Base64url encode (without padding)
  const base64UrlEncode = (obj: any): string => {
    return btoa(JSON.stringify(obj))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };
  
  const encodedHeader = base64UrlEncode(header);
  const encodedPayload = base64UrlEncode(payload);
  
  // For debug mode, we use an unsigned JWT (signature is empty)
  return `${encodedHeader}.${encodedPayload}.`;
}

export function DebugAuthProvider({ children }: DebugAuthProviderProps) {
  const [authState, setAuthState] = useState<MockAuthState>({
    isAuthenticated: false,
    isLoading: true,
    error: null,
    accessToken: null,
    user: null,
  });

  useEffect(() => {
    // Simulate auth initialization delay
    const timer = setTimeout(() => {
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        error: null,
        accessToken: createDebugJWT(),
        user: {
          sub: 'debug-user-123',
          name: 'Debug User',
          email: 'debug@example.com',
          preferred_username: 'debug-user',
        },
      });
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const contextValue: DebugAuthContextType = {
    authState,
    oktaAuth: {
      getAccessToken: async () => authState.accessToken || undefined,
    },
  };

  return (
    <DebugAuthContext.Provider value={contextValue}>
      {children}
    </DebugAuthContext.Provider>
  );
}

export function useDebugAuth(): DebugAuthContextType {
  const context = useContext(DebugAuthContext);
  if (!context) {
    throw new Error('useDebugAuth must be used within a DebugAuthProvider');
  }
  return context;
}

export { isDebugModeEnabled };
